import {
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Text,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { MdClose, MdDescription } from "react-icons/md";
import { useSelector } from "react-redux";
import VideoPreview from "../../containers/student/video/videoPreview";
import axiosInstance from "../../helpers/api";
import { studentPalette } from "../../constants/studentPalette";
import { lessonPdfPublicUrl } from "./PdfPreviewModal";

const MODAL_BG = "#3d1824";
const ACCENT = studentPalette.crimson;
const CREAM = studentPalette.cream;

const CourseVideoModal = ({
  isOpen,
  onClose,
  chapterLabel,
  videoId,
  initialVideoFiles,
  videoTitleFallback,
  onOpenPdf,
}) => {
  const videoDetails = useSelector((s) => s.chapterVideos.videoDetails);
  const [otp, setOtp] = useState("");
  const [playBack, setPlayBack] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  const active =
    videoDetails && videoId != null && String(videoDetails.id) === String(videoId)
      ? videoDetails
      : null;

  const relatedPdfFiles = useMemo(() => {
    const fromStore = active?.files;
    const fromList = initialVideoFiles;
    if (Array.isArray(fromStore) && fromStore.length > 0) return fromStore;
    if (Array.isArray(fromList) && fromList.length > 0) return fromList;
    return [];
  }, [active?.files, initialVideoFiles]);

  useEffect(() => {
    if (!isOpen || !active?.id) {
      setOtp("");
      setPlayBack("");
      return;
    }
    if (active.video_type === 1) {
      let cancelled = false;
      setOtpLoading(true);
      axiosInstance
        .get(`/students/videos/otp/${active.id}`)
        .then((res) => {
          if (!cancelled) {
            setOtp(res?.data?.otp ?? "");
            setPlayBack(res?.data?.playbackInfo ?? "");
          }
        })
        .catch(() => {
          if (!cancelled) {
            setOtp("");
            setPlayBack("");
          }
        })
        .finally(() => {
          if (!cancelled) {
            setTimeout(() => setOtpLoading(false), 400);
          }
        });
      return () => {
        cancelled = true;
      };
    }
    setOtp("");
    setPlayBack("");
    setOtpLoading(false);
  }, [isOpen, active?.id, active?.video_type]);

  const title = active?.title || videoTitleFallback || "Session";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      motionPreset="slideInBottom"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="rgba(91, 13, 22, 0.92)" backdropFilter="blur(8px)" />
      <ModalContent
        bg="transparent"
        boxShadow="none"
        mx={{ base: 2, md: 8 }}
        my={{ base: 4, md: 8 }}
        maxW="7xl"
        w="100%"
      >
        <ModalBody p={0}>
          <VStack align="stretch" spacing={{ base: 4, md: 6 }} maxH={{ base: "92vh", md: "calc(100vh - 4rem)" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={4}
              px={{ base: 1, md: 2 }}
            >
              <Box>
                <Text
                  color="white"
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="800"
                  letterSpacing="tight"
                  lineHeight="1.2"
                >
                  Lesson archive
                </Text>
                <Text
                  fontSize="10px"
                  fontWeight="800"
                  color={ACCENT}
                  textTransform="uppercase"
                  letterSpacing="0.2em"
                  mt={1}
                >
                  {chapterLabel || "Lesson"}
                </Text>
                <Text color={`${CREAM}cc`} fontSize="sm" fontWeight="600" mt={2} noOfLines={2}>
                  {title}
                </Text>
              </Box>
              <IconButton
                aria-label="Close video"
                icon={<MdClose size={22} />}
                onClick={onClose}
                borderRadius="lg"
                bg="whiteAlpha.200"
                color="white"
                _hover={{ bg: "whiteAlpha.300" }}
                flexShrink={0}
              />
            </Box>

            <Box
              flex={1}
              minH={0}
              overflow="auto"
              bg={MODAL_BG}
              borderRadius="2xl"
              borderWidth="1px"
              borderColor="whiteAlpha.200"
              boxShadow="0 25px 50px -12px rgba(0,0,0,0.45)"
              p={{ base: 4, md: 6 }}
            >
              <VideoPreview
                key={videoId}
                videoId={videoId}
                variant="modal"
                loading={otpLoading}
                otp={otp}
                playBack={playBack}
              />

              {relatedPdfFiles.length > 0 ? (
                <Box mt={{ base: 5, md: 6 }} pt={{ base: 4, md: 5 }} borderTopWidth="1px" borderColor="whiteAlpha.200">
                  <Text
                    fontSize="10px"
                    fontWeight="800"
                    color={ACCENT}
                    textTransform="uppercase"
                    letterSpacing="0.18em"
                    mb={3}
                  >
                    Related PDFs
                  </Text>
                  <Flex gap={2} flexWrap="wrap">
                    {relatedPdfFiles.map((f, i) => {
                      const url = lessonPdfPublicUrl(f?.file);
                      const label = `File #${i + 1}`;
                      return (
                        <Button
                          key={f?.id ?? `${videoId}-pdf-${i}`}
                          size="sm"
                          leftIcon={<MdDescription size={18} />}
                          variant="outline"
                          borderColor="whiteAlpha.400"
                          color={CREAM}
                          fontWeight="700"
                          borderRadius="lg"
                          px={4}
                          py={5}
                          h="auto"
                          whiteSpace="normal"
                          textAlign="left"
                          isDisabled={!url}
                          _hover={{
                            bg: "whiteAlpha.100",
                            borderColor: "whiteAlpha.600",
                          }}
                          _disabled={{ opacity: 0.45, cursor: "not-allowed" }}
                          onClick={() => {
                            if (!url || !onOpenPdf) return;
                            onOpenPdf({
                              url,
                              title: `${title} — ${label}`,
                              backLabel: "Back to lesson",
                            });
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Flex>
                </Box>
              ) : null}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default CourseVideoModal;
